import {
  DEFAULT_REACT_GRID_LAYOUT_SETTINGS,
  type ReactGridLayoutSettings,
} from "../../grid/settings/reactGridLayoutSettings.types";
import type { WidgetKind } from "../widgetKind";
import {
  DESK_STANDARD_WIDTH_COLS,
  ORDER_TICKET_FIXED_WIDTH_PX,
  RGL_COLS,
  WATCHLIST_MIN_WIDTH_PX,
  WIDGET_PLACEMENT_DEFAULTS,
} from "./placementAdapters";
import type { WidgetPlacement, WorkspaceSnapshot } from "./workspace.types";

export const WORKSPACE_TEMPLATE_ID_VALUES = [
  "equitiesTemplateA",
  "equitiesTemplateB",
  "optionsTemplateA",
  "optionsTemplateB",
  "portfolioRiskA",
  "portfolioRiskB",
] as const;

export type WorkspaceTemplateId = (typeof WORKSPACE_TEMPLATE_ID_VALUES)[number];

export interface WorkspaceTemplateDefinition {
  label: string;
  workspace: WorkspaceSnapshot;
  /** Overrides merged onto DEFAULT_REACT_GRID_LAYOUT_SETTINGS at create time. */
  gridSettings: Partial<ReactGridLayoutSettings>;
}

type TemplateTile = {
  id: string;
  kind: WidgetKind;
  x: number;
  y: number;
  w: number;
  h: number;
  fixedWidthPx?: number;
  minWidthPx?: number;
  alignX?: "end";
};

const tile = (
  id: string,
  kind: WidgetKind,
  ...[x, y, w, h]: [x: number, y: number, w: number, h: number]
): TemplateTile => ({ id, kind, x, y, w, h });

const deskWatchlistTile = (
  id: string,
  x: number,
  y: number,
  h: number,
): TemplateTile => ({
  id,
  kind: "watchlist",
  x,
  y,
  w: DESK_STANDARD_WIDTH_COLS,
  h,
  minWidthPx: WATCHLIST_MIN_WIDTH_PX,
});

/** 420px ticket pinned to the right of its own band — not a full-height rail. */
const rightEdgeOrderTicketTile = (
  id: string,
  y: number,
  h: number,
): TemplateTile => ({
  id,
  kind: "orderTicket",
  x: RGL_COLS - DESK_STANDARD_WIDTH_COLS,
  y,
  w: DESK_STANDARD_WIDTH_COLS,
  h,
  fixedWidthPx: ORDER_TICKET_FIXED_WIDTH_PX,
  alignX: "end",
});

const createTemplateSnapshot = (
  tiles: TemplateTile[],
  widgetSettings: WorkspaceSnapshot["widgetSettings"] = {},
): WorkspaceSnapshot => ({
  engine: "rgl",
  widgets: tiles.map(({ id, kind }) => ({ id, kind })),
  placements: tiles.map(
    ({
      id,
      kind,
      x,
      y,
      w,
      h,
      fixedWidthPx,
      minWidthPx,
      alignX,
    }): WidgetPlacement => ({
      widgetId: id,
      x,
      y,
      w,
      h,
      minW: WIDGET_PLACEMENT_DEFAULTS[kind].minW,
      minH: WIDGET_PLACEMENT_DEFAULTS[kind].minH,
      ...(fixedWidthPx === undefined ? {} : { fixedWidthPx }),
      ...(minWidthPx === undefined ? {} : { minWidthPx }),
      ...(alignX === undefined ? {} : { alignX }),
    }),
  ),
  widgetSettings,
});

const defineTemplate = (
  label: string,
  workspace: WorkspaceSnapshot,
  gridSettings: Partial<ReactGridLayoutSettings> = {},
): WorkspaceTemplateDefinition => ({
  label,
  workspace,
  gridSettings,
});

/** Equities / Options keep authored row gaps; Portfolio Risk uses vertical compact. */
const NO_COMPACT: Partial<ReactGridLayoutSettings> = { compactor: "none" };

export const WORKSPACE_TEMPLATES: Record<
  WorkspaceTemplateId,
  WorkspaceTemplateDefinition
> = {
  equitiesTemplateA: defineTemplate(
    "Equities Template A",
    createTemplateSnapshot(
      [
        deskWatchlistTile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa01", 0, 0, 16),
        tile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa02", "chart", 7, 0, 10, 16),
        rightEdgeOrderTicketTile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa03", 0, 16),
        tile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa04", "positions", 0, 16, 12, 8),
        tile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa05", "orders", 12, 16, 12, 8),
        tile("aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa06", "balances", 0, 24, 8, 7),
        tile(
          "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa07",
          "portfolioHistory",
          8,
          24,
          8,
          7,
        ),
        tile(
          "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa08",
          "activities",
          16,
          24,
          8,
          7,
        ),
      ],
      {
        "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa07": {
          kind: "portfolioHistory",
          comparisons: [],
        },
      },
    ),
    NO_COMPACT,
  ),
  equitiesTemplateB: defineTemplate(
    "Equities Template B",
    createTemplateSnapshot(
      [
        tile("cccccccc-cccc-4ccc-8ccc-cccccccccc01", "chart", 0, 0, 10, 11),
        rightEdgeOrderTicketTile("cccccccc-cccc-4ccc-8ccc-cccccccccc02", 0, 11),
        deskWatchlistTile("cccccccc-cccc-4ccc-8ccc-cccccccccc03", 10, 0, 11),
        tile("cccccccc-cccc-4ccc-8ccc-cccccccccc04", "positions", 0, 11, 12, 7),
        tile("cccccccc-cccc-4ccc-8ccc-cccccccccc05", "orders", 12, 11, 12, 7),
        tile(
          "cccccccc-cccc-4ccc-8ccc-cccccccccc06",
          "portfolioHistory",
          0,
          18,
          8,
          6,
        ),
        tile("cccccccc-cccc-4ccc-8ccc-cccccccccc07", "balances", 8, 18, 8, 6),
        tile(
          "cccccccc-cccc-4ccc-8ccc-cccccccccc08",
          "activities",
          16,
          18,
          8,
          6,
        ),
      ],
      {
        "cccccccc-cccc-4ccc-8ccc-cccccccccc06": {
          kind: "portfolioHistory",
          comparisons: [],
        },
      },
    ),
    NO_COMPACT,
  ),
  optionsTemplateA: defineTemplate(
    "Options Template A",
    createTemplateSnapshot([
      deskWatchlistTile("dddddddd-dddd-4ddd-8ddd-dddddddddd01", 0, 0, 16),
      tile(
        "dddddddd-dddd-4ddd-8ddd-dddddddddd02",
        "optionsChain",
        7,
        0,
        10,
        16,
      ),
      rightEdgeOrderTicketTile("dddddddd-dddd-4ddd-8ddd-dddddddddd03", 0, 16),
      tile("dddddddd-dddd-4ddd-8ddd-dddddddddd04", "chart", 0, 16, 8, 9),
      tile("dddddddd-dddd-4ddd-8ddd-dddddddddd05", "positions", 8, 16, 8, 9),
      tile("dddddddd-dddd-4ddd-8ddd-dddddddddd06", "orders", 16, 16, 8, 9),
      tile("dddddddd-dddd-4ddd-8ddd-dddddddddd07", "balances", 0, 25, 12, 7),
      tile("dddddddd-dddd-4ddd-8ddd-dddddddddd08", "activities", 12, 25, 12, 7),
    ]),
    NO_COMPACT,
  ),
  optionsTemplateB: defineTemplate(
    "Options Template B",
    createTemplateSnapshot([
      tile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee01", "positions", 0, 0, 10, 12),
      tile(
        "eeeeeeee-eeee-4eee-8eee-eeeeeeeeee02",
        "optionsChain",
        10,
        0,
        14,
        12,
      ),
      tile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee03", "chart", 0, 12, 10, 11),
      deskWatchlistTile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee04", 10, 12, 11),
      rightEdgeOrderTicketTile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee05", 12, 11),
      tile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee06", "orders", 0, 23, 9, 8),
      tile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee07", "activities", 9, 23, 9, 8),
      tile("eeeeeeee-eeee-4eee-8eee-eeeeeeeeee08", "balances", 18, 23, 6, 8),
    ]),
    NO_COMPACT,
  ),
  portfolioRiskA: defineTemplate(
    "Portfolio Risk Template A",
    createTemplateSnapshot(
      [
        tile("88888888-8888-4888-8888-888888888801", "positions", 0, 0, 16, 16),
        tile("88888888-8888-4888-8888-888888888802", "balances", 16, 0, 8, 16),
        tile(
          "88888888-8888-4888-8888-888888888803",
          "portfolioHistory",
          0,
          24,
          24,
          10,
        ),
        tile("88888888-8888-4888-8888-888888888804", "orders", 0, 16, 12, 8),
        tile(
          "88888888-8888-4888-8888-888888888805",
          "activities",
          12,
          16,
          12,
          8,
        ),
      ],
      {
        "88888888-8888-4888-8888-888888888803": {
          kind: "portfolioHistory",
          comparisons: [],
        },
      },
    ),
  ),
  portfolioRiskB: defineTemplate(
    "Portfolio Risk Template B",
    createTemplateSnapshot(
      [
        tile("99999999-9999-4999-8999-999999999901", "balances", 0, 0, 8, 12),
        tile("99999999-9999-4999-8999-999999999902", "positions", 8, 0, 16, 12),
        tile(
          "99999999-9999-4999-8999-999999999903",
          "portfolioHistory",
          0,
          20,
          24,
          10,
        ),
        tile("99999999-9999-4999-8999-999999999904", "orders", 0, 12, 12, 8),
        tile(
          "99999999-9999-4999-8999-999999999905",
          "activities",
          12,
          12,
          12,
          8,
        ),
      ],
      {
        "99999999-9999-4999-8999-999999999903": {
          kind: "portfolioHistory",
          comparisons: [],
        },
      },
    ),
  ),
};

export const WORKSPACE_TEMPLATE_OPTIONS = WORKSPACE_TEMPLATE_ID_VALUES.map(
  (id) => ({
    id,
    label: WORKSPACE_TEMPLATES[id].label,
  }),
);

/** Full settings blob for a new workspace entry. */
export const resolveWorkspaceTemplateSettings = (
  id: WorkspaceTemplateId,
): ReactGridLayoutSettings => ({
  ...DEFAULT_REACT_GRID_LAYOUT_SETTINGS,
  ...WORKSPACE_TEMPLATES[id].gridSettings,
});
